import { TablePagination } from "@mui/material";
import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQueryParams } from "../../hooks";
import { DeactivateUserModal } from "../../components";
import { getHiringRequests, deleteHiringRequest } from "./Helper";

const Flexpert = () => {
  const context = useOutletContext();
  const [hiringRequests, setHiringRequests] = useState([]);
  const { getParam, setMultiParam } = useQueryParams();
  const currentPage = getParam("page");
  const currentLimit = getParam("pageSize");
  const [requestsPage, setRequestsPage] = useState(parseInt(currentPage) || 0);
  const [requestsLimit, setRequestsLimit] = useState(parseInt(currentLimit) || 10);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [toggle, setToggle] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [requestInfo, setRequestInfo] = useState({ id: "" });
  const [expandedSkills, setExpandedSkills] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  const handleChangePage = (event, newPage) => {
    fetchData(newPage, requestsLimit);
    setRequestsPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRequestsLimit(parseInt(event.target.value, 10));
    setRequestsPage(0);
    fetchData(0, parseInt(event.target.value, 10));
  };

  const fetchData = (page, limit) => {
    getHiringRequests(page, limit)
      .then((response) => {
        setHiringRequests(response.data?.data?.data || []);
        setRequestsTotal(response.data?.data?.meta?.total || 0);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    context(["Flexpert Enquiry"]);
    getHiringRequests(requestsPage, requestsLimit)
      .then((response) => {
        setHiringRequests(response.data?.data?.data || []);
        setRequestsTotal(response.data?.data?.meta?.total || 0);
      })
      .catch((err) => console.log("Error:", err));
  }, [toggle]);

  useEffect(() => {
    const cmsPermissions = localStorage.getItem('CMSUsersPermission');
    if (cmsPermissions) {
      setIsAdmin(false);
    } else {
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    setMultiParam({"page": requestsPage, "pageSize": requestsLimit});
  }, [requestsPage, requestsLimit]);

  const handleDelete = () => {
    deleteHiringRequest(requestInfo.id)
      .then((data) => {
        setToggle(!toggle);
        setOpenDeleteModal(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    });
  };

  const formatDateTimeLocal = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const toggleSkillsExpansion = (requestId) => {
    setExpandedSkills(prev => ({
      ...prev,
      [requestId]: !prev[requestId]
    }));
  };

  const renderSkills = (skills, requestId) => {
    if (!skills || skills.length === 0) return "-";
    
    const isExpanded = expandedSkills[requestId];
    const maxSkillsToShow = 2;
    
    if (skills.length <= maxSkillsToShow) {
      return skills.join(", ");
    }
    
    if (isExpanded) {
      return (
        <div>
          {skills.join(", ")}
          <button 
            onClick={() => toggleSkillsExpansion(requestId)}
            className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
          >
            less
          </button>
        </div>
      );
    } else {
      return (
        <div>
          {skills.slice(0, maxSkillsToShow).join(", ")}
          <button 
            onClick={() => toggleSkillsExpansion(requestId)}
            className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
          >
            +{skills.length - maxSkillsToShow} more
          </button>
        </div>
      );
    }
  };

  const formatInterestedIn = (interestedIn) => {
    if (!interestedIn) return "-";
    return interestedIn
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatUtmParams = (utmParams) => {
    if (!utmParams || typeof utmParams !== "object") return "DIRECT";

    const sanitizedEntries = Object.entries(utmParams).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    );

    if (sanitizedEntries.length === 0) return "DIRECT";

    const priorityOrder = ["utm_source", "utm_campaign", "utm_device"];
    const normalizeKey = (key) =>
      key?.toString().toLowerCase().replace(/-/g, "_") || "";

    const prioritizedValues = priorityOrder
      .map((priorityKey) => {
        const match = sanitizedEntries.find(
          ([key]) => normalizeKey(key) === priorityKey
        );
        return match ? match[1] : null;
      })
      .filter((value) => value !== null);

    const remainingValues = sanitizedEntries
      .filter(
        ([key]) => !priorityOrder.includes(normalizeKey(key))
      )
      .map(([, value]) => value);

    const values = [...prioritizedValues, ...remainingValues];

    return values.length > 0 ? values.join(" | ") : "DIRECT";
  };

  return (
    <div className="p-5 shadow-md bg-white">
      {openDeleteModal && (
        <DeactivateUserModal 
          open={openDeleteModal} 
          setOpen={setOpenDeleteModal} 
          title="Do you want to delete the user query?" 
          deactivateUser={handleDelete}
          showMessage={false}
        />
      )}
      
      <div className="overflow-x-auto relative">
        <table className="w-full text-sm text-left text-gray-500 table-fixed">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="py-4 px-4 w-16 text-center">
                S No.
              </th>
              <th scope="col" className="py-4 px-4 w-44">
                Client Name
              </th>
              <th scope="col" className="py-4 px-4 w-48">
                Email
              </th>
              <th scope="col" className="py-4 px-4 w-44">
                Phone Number
              </th>
              <th scope="col" className="py-4 px-4 w-40">
                Company Name
              </th>
              <th scope="col" className="py-4 px-4 w-48">
                Source
              </th>
              <th scope="col" className="py-4 px-4 w-48">
                Interested In
              </th>
              <th scope="col" className="py-4 px-4 w-40">
                Enquired On
              </th>
              <th scope="col" className="py-4 px-4 w-40">
                Local Time
              </th>
              <th scope="col" className="py-4 px-4 w-44">
                Timezone
              </th>
              <th scope="col" className="py-4 px-4 w-48">
                Skills Preference
              </th>
              <th scope="col" className="py-4 px-4 w-28">
                Work Type
              </th>
              <th scope="col" className="py-4 px-4 w-32 text-center">
                Request For Call
              </th>
              <th scope="col" className="py-4 px-4 w-32 text-center">
                Is Verified
              </th>
            {isAdmin && (
              <th scope="col" className="py-4 px-4 w-24 text-center">
                Action
              </th>
            )}
            </tr>
          </thead>
          <tbody>
            {Array.isArray(hiringRequests) && hiringRequests.map((request, index) => {
              return (
                <tr className="bg-white border-b hover:bg-gray-50" key={index}>
                  <td className="py-4 px-4 font-medium text-gray-900 text-center">
                    {requestsPage * requestsLimit + index + 1}
                  </td>
                  <td className="py-4 px-4 truncate" title={request?.name || "-"}>
                    {request?.name || "-"}
                  </td>
                  <td className="py-4 px-4 truncate" title={request?.email || "-"}>
                    {request?.email || "-"}
                  </td>
                  <td className="py-4 px-4">
                    <div className="break-words">
                      {request?.countryCode && request?.phone
                        ? `${request.countryCode.startsWith('+') ? '' : '+'}${request.countryCode} ${request.phone}`
                        : request?.phone || "-"}
                    </div>
                  </td>
                  <td className="py-4 px-4 truncate" title={request?.companyName || "-"}>
                    {request?.companyName || "-"}
                  </td>
                <td className="py-4 px-4 break-words">
                  {formatUtmParams(request?.utm_params)}
                </td>
                  <td className="py-4 px-4">
                    <div className="break-words">
                      {formatInterestedIn(request?.interested_in)}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-xs leading-relaxed">
                      {request?.enquiredAt ? formatDateTime(request.enquiredAt) : 
                      request?.createdAt ? formatDateTime(request.createdAt) : "-"}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-xs leading-relaxed">
                      {request?.createdAt ? formatDateTimeLocal(request.createdAt) : "-"}
                    </div>
                  </td>
                  <td className="py-4 px-4 truncate" title={request?.timezone || "-"}>
                    {request?.timezone || "-"}
                  </td>
                  <td className="py-4 px-4">
                    <div className="break-words text-xs leading-relaxed">
                      {renderSkills(request?.skills, request?.id)}
                    </div>
                  </td>
                  <td className="py-4 px-4 truncate" title={request?.workType || "-"}>
                    {request?.workType || "-"}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs inline-block ${
                      request?.requestForCall 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {request?.requestForCall ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs inline-block ${
                      request?.isSignUp 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {request?.isSignUp ? "Yes" : "No"}
                    </span>
                  </td>
                {isAdmin && (
                  <td className="py-4 px-4 text-center">
                    <Button
                      size="small"
                      style={{ color: "red", borderColor: "red" }}
                      variant="outlined"
                      className="!text-xs !min-w-0 !px-2"
                      onClick={() => {
                        setOpenDeleteModal(true);
                        setRequestInfo({ id: request?.id });
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hiringRequests.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 text-lg">No Data Available</p>
        </div>
      )}

      {hiringRequests.length > 0 && (
        <TablePagination
          component="div"
          count={requestsTotal}
          page={requestsPage}
          onPageChange={handleChangePage}
          rowsPerPage={requestsLimit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      )}
    </div>
  );
};

export default Flexpert;